import React, { useEffect, useState } from 'react'
import { registerComponent } from 'react-register-dom'
import ButtonLink from '@components/ButtonLink'
import Logo, { LogoProperties } from '@components/Header/Logo'
import ProductWishlistItem from '@components/Wishlist/ProductWishlistWidget/ProductWishlistItem'
import { ProductWishlistConfig } from '@components/Wishlist/ProductWishlistWidget/ProductWishlistWidget'
import { getProductsForWishlist } from '@components/Wishlist/SharedWishlistStorage'
import { WishlistProduct } from '@components/Wishlist/WishlistStorage'

const getDefaultLogoData = (): LogoProperties => ({
  backgroundImage: 'https://assets.franke.com/is/content/frankemanagement/logo-box-1',
  foregroundImage: 'https://assets.franke.com/is/content/frankemanagement/logo-text-1',
  altText: 'Logo',
  logoLink: '/',
})

const parseConfigData = (
  isNewHeader: boolean,
  parsedData: any,
): { config: ProductWishlistConfig; logo: LogoProperties } => {
  if (isNewHeader) {
    return {
      config: parsedData,
      logo: parsedData.logo ?? getDefaultLogoData(),
    }
  }

  const { logo, productwishlistwidget } = parsedData[':items']
  return {
    config: productwishlistwidget,
    logo,
  }
}

const SharedProductWishlist: React.FC = () => {
  const [wishlist, setWishlist] = useState<WishlistProduct[] | undefined>()
  const [config, setConfig] = useState<ProductWishlistConfig | undefined>()
  const [logo, setLogo] = useState<LogoProperties | undefined>()

  useEffect(() => {
    const loadConfigAndData = async () => {
      const body = document.querySelector('body') as HTMLElement | null
      body?.classList.add('wishlist-print', 'wishlist-print--shared')

      const isNewHeader = Boolean(document.querySelector('.cmp-header-v2') as HTMLElement | null)
      const headerElement = document.querySelector('.cmp-header') as HTMLElement | null

      if ((!isNewHeader && !headerElement) || (isNewHeader && headerElement)) {
        throw new Error(
          'Something is wrong with header selector. Please verify .cmp-header-v2 or .cmp-header exists OR both are not present at the same time.',
        )
      }

      const jsonData =
        (isNewHeader
          ? document.querySelector('.cmp-shared-product-wishlist')?.getAttribute('data-cmp-data-json')
          : headerElement?.dataset.cmpDataJson) || null

      if (!jsonData) {
        throw new Error('Missing wishlist widget JSON.')
      }

      const parsedData = JSON.parse(jsonData)
      const { config: configData, logo: logoData } = parseConfigData(isNewHeader, parsedData)

      if (!configData) {
        throw new Error('Missing wishlish widget config.')
      }

      setConfig(configData)
      setLogo(logoData)

      const data: any = await getProductsForWishlist(configData.productWishlistEndpointUrl)
      setWishlist(data.products)
    }

    loadConfigAndData()
  }, [])

  return (
    <div className="cmp-product-shared-wishlist">
      <div className="cmp-product-shared-wishlist__header">
        {logo && (
          <Logo
            backgroundImage={logo.backgroundImage}
            foregroundImage={logo.foregroundImage}
            altText={logo.altText}
            logoLink={logo.logoLink}
          />
        )}
        <h3 className="cmp-product-wishlist__title">{config?.overlayTitleLabel}</h3>
      </div>
      {wishlist && config ? (
        <div className="cmp-product-wishlist">
          {wishlist.length > 0 && (
            <div className="cmp-product-wishlist__actions">
              <p className="cmp-product-wishlist__sum">
                <span className="cmp-product-wishlist__sum--number">{wishlist.length}</span>{' '}
                {wishlist.length > 1 ? config.pluralProductsLabel : config.singleProductLabel}
              </p>
              <ButtonLink
                className="cmp-product-wishlist__button"
                text={config.saveAsPdfButtonLabel}
                icon="ri-download-line"
                onClick={() => window.print()}
              />
            </div>
          )}
          <div className="cmp-product-wishlist__products">
            {wishlist.map((wishlistProduct) => (
              <ProductWishlistItem
                productWishlistItem={wishlistProduct}
                productButtonLabel={config.productButtonLabel}
                productSheetLabel={config.productSheetLabel}
                priceVatLabel={config.priceVatLabel}
                articleNumberLabel={config.articleNumberLabel}
                key={wishlistProduct.sku}
                baseProductSheetProxyUrl={config.baseProductSheetProxyUrl}
                removeProductLabel={config.removeProductButtonLabel}
                isSharedWishlist={true}
                errorMessageLabel={config.errorMessageLabel}
                baseProductUrl={config.baseProductUrl}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="cmp-product-wishlist__loading">
          <p>
            Loading wishlist
            <span className="cmp-product-wishlist__loading-dots">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </p>
        </div>
      )}
    </div>
  )
}

registerComponent('SharedProductWishlist', SharedProductWishlist)
export default SharedProductWishlist
